import { useEffect, useState } from "react";

import { Loader, Table } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { EmptyMessage, Link, Modal, Text } from "components";
import { RouteConstants } from "constant";
import { DashboardService } from "services";
import {
  DashboardAgentFilterParams,
  DashboardDelayedProcessingAlarmEntryResponse,
  DashboardScope,
} from "types";

import classes from "./OverdueAlertSection.module.css";

interface OverdueAlertSectionProps {
  className?: string;
  scope?: DashboardScope;
  selectedAgentIds?: number[] | null;
}

const { ROUTE_CLIENTS } = RouteConstants;

const OverdueAlertSection = ({
  className,
  scope = "admin",
  selectedAgentIds,
}: OverdueAlertSectionProps) => {
  const { t } = useTranslation();
  const [overdueCount, setOverdueCount] = useState<number | null>(null);
  const [countLoadError, setCountLoadError] = useState(false);
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [details, setDetails] = useState<DashboardDelayedProcessingAlarmEntryResponse[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsLoadError, setDetailsLoadError] = useState(false);
  const resolvedOverdueCount = overdueCount ?? 0;
  const hasOverdueItems = resolvedOverdueCount > 0;

  useEffect(() => {
    let mounted = true;
    const params: DashboardAgentFilterParams | undefined = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadDelayedProcessingAlarmCount = async () => {
      try {
        const data = await DashboardService.getDelayedProcessingAlarmCount(scope, params);

        if (!mounted) {
          return;
        }

        setCountLoadError(false);
        setOverdueCount(data.count);
      } catch {
        if (!mounted) {
          return;
        }

        setCountLoadError(true);
      }
    };

    loadDelayedProcessingAlarmCount();

    return () => {
      mounted = false;
    };
  }, [scope, selectedAgentIds]);

  useEffect(() => {
    if (!detailsOpened) {
      return;
    }

    let mounted = true;
    const params: DashboardAgentFilterParams | undefined = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadDelayedProcessingAlarmInfo = async () => {
      setDetailsLoading(true);
      setDetailsLoadError(false);
      setDetails([]);

      try {
        const data = await DashboardService.getDelayedProcessingAlarmInfo(scope, params);

        if (!mounted) {
          return;
        }

        setDetails(data);
      } catch {
        if (!mounted) {
          return;
        }

        setDetailsLoadError(true);
      } finally {
        if (mounted) {
          setDetailsLoading(false);
        }
      }
    };

    loadDelayedProcessingAlarmInfo();

    return () => {
      mounted = false;
    };
  }, [detailsOpened, scope, selectedAgentIds]);

  return (
    <>
      <button
        type="button"
        disabled={!hasOverdueItems}
        onClick={() => setDetailsOpened(true)}
        aria-haspopup={hasOverdueItems ? "dialog" : undefined}
        aria-label={hasOverdueItems ? t("dashboard.admin.overdue.hasOverdue") : undefined}
        className={[
          classes.root,
          countLoadError || hasOverdueItems ? classes.danger : classes.success,
          hasOverdueItems ? classes.interactive : undefined,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {overdueCount === null && !countLoadError ? (
          <Loader color="#ffffff" type="oval" size={36} />
        ) : countLoadError ? (
          <Text className={classes.label}>
            {t("dashboard.admin.overdue.loadError")}
          </Text>
        ) : hasOverdueItems ? (
          <>
            <Text className={classes.count}>{resolvedOverdueCount}</Text>
            <Text className={classes.label}>
              {t("dashboard.admin.overdue.hasOverdue")}
            </Text>
          </>
        ) : (
          <>
            <IconCheck className={classes.icon} size={42} stroke={3} aria-hidden="true" />
            <Text className={classes.label}>
              {t("dashboard.admin.overdue.noOverdue")}
            </Text>
          </>
        )}
      </button>

      <Modal
        opened={detailsOpened}
        onClose={() => setDetailsOpened(false)}
        title={t("dashboard.admin.overdue.hasOverdue")}
        size="xl"
        styles={{
          header: {
            padding: "16px",
          },
          body: {
            padding: "0 8px 16px",
          },
        }}
      >
        {detailsLoading ? (
          <div className={classes.modalState}>
            <Loader size={32} type="oval" />
          </div>
        ) : detailsLoadError ? (
          <EmptyMessage message={t("dashboard.admin.overdue.listLoadError")} marginTop={0} />
        ) : details.length === 0 ? (
          <EmptyMessage message={t("results.no")} marginTop={0} />
        ) : (
          <div className={classes.tableWrapper}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("credit.previousRequests.table.id")}</Table.Th>
                  <Table.Th>{t("user.fullName.label")}</Table.Th>
                  <Table.Th>{t("credit.agent.label")}</Table.Th>
                  <Table.Th>{t("credit.status.label")}</Table.Th>
                  <Table.Th>{t("credit.previousRequests.table.statusChangedAt")}</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {details.map((item) => (
                  <Table.Tr key={item.creditRequestId}>
                    <Table.Td>{item.creditRequestId}</Table.Td>
                    <Table.Td>
                      <Link to={`${ROUTE_CLIENTS}/${item.clientId}`}>{item.clientName}</Link>
                    </Table.Td>
                    <Table.Td>{item.agent?.fullName ?? "-"}</Table.Td>
                    <Table.Td>{t(`credit.status.name.${item.status}`)}</Table.Td>
                    <Table.Td>
                      {item.statusChanged
                        ? t("format.date_time2", { date: new Date(item.statusChanged) })
                        : "-"}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Modal>
    </>
  );
};

export default OverdueAlertSection;
